import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { formatCurrency } from '../../utils/campusMinistryFields';

// Fall Retreat form: every field from the paper "Rowan Catholic Fall Retreat
// Form" (roommates, two emergency contacts, doctor, medications, medical
// conditions, allergies, insurance, consent + signature) plus email.
// The page shell (CampusMinistryRegistration) owns the header, stepper and
// the payment step.

const emptyContact = () => ({ name: '', address: '', phoneDay: '', phoneEvening: '' });
const emptyMedication = () => ({ name: '', prescribingPhysician: '', phone: '' });

export const initialForm = () => ({
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  studentCategory: '',
  roommateChoices: ['', '', ''],
  emergencyContacts: [emptyContact(), emptyContact()],
  primaryDoctor: { name: '', phone: '' },
  medications: [emptyMedication()],
  medicalConditions: '',
  allergies: { medicines: '', other: '' },
  insurance: { carrier: '', plan: '', policyNumber: '' },
  consent: { agreed: false },
  signature: { name: '' }
});

export const steps = ['About You', 'Emergency Contacts', 'Medical & Insurance', 'Consent & Signature'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Returns an error message for the step, or null when it may continue.
export const validateStep = (step, form) => {
  if (step === 0) {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.dateOfBirth) {
      return 'Please enter your first name, last name and date of birth.';
    }
    if (!EMAIL_RE.test(form.email.trim())) return 'Please enter a valid email address.';
    if (!form.studentCategory) return 'Please select which student price applies to you.';
  }
  if (step === 1) {
    const c = form.emergencyContacts[0];
    if (!c.name.trim() || !c.phoneDay.trim()) {
      return 'Please enter at least one emergency contact with a daytime phone number.';
    }
  }
  if (step === 3) {
    if (!form.consent.agreed) return 'Please read and agree to the Consent to Medical Treatment, Release and Indemnity.';
    if (!form.signature.name.trim()) return 'Please type your full name as your signature.';
  }
  return null;
};

// Drops blank roommate / contact / medication rows before submitting.
export const toPayload = (form) => ({
  ...form,
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  email: form.email.trim(),
  roommateChoices: form.roommateChoices.map(s => s.trim()).filter(Boolean),
  emergencyContacts: form.emergencyContacts.filter(c => c.name.trim() || c.phoneDay.trim() || c.address.trim() || c.phoneEvening.trim()),
  medications: form.medications.filter(m => m.name.trim() || m.prescribingPhysician.trim() || m.phone.trim()),
  signature: { name: form.signature.name.trim() }
});

export const testData = () => ({
  ...initialForm(),
  firstName: 'Test',
  lastName: 'Retreatant',
  dateOfBirth: '2005-04-09',
  email: 'test.retreatant@example.com',
  studentCategory: 'rowan-undergrad',
  roommateChoices: ['Roommate One', 'Roommate Two', ''],
  emergencyContacts: [
    { name: 'Parent One', address: '1 Main St, Glassboro NJ 08028', phoneDay: '856-555-0101', phoneEvening: '' },
    { name: 'Parent Two', address: '1 Main St, Glassboro NJ 08028', phoneDay: '856-555-0102', phoneEvening: '' }
  ],
  primaryDoctor: { name: 'Dr. Test', phone: '856-555-0199' },
  medications: [{ name: 'N/A', prescribingPhysician: '', phone: '' }],
  medicalConditions: 'N/A',
  allergies: { medicines: 'None', other: 'None' },
  insurance: { carrier: 'Test Health', plan: 'PPO', policyNumber: 'TEST123' },
  consent: { agreed: true },
  signature: { name: 'Test Retreatant' }
});

// Presentational helpers stay at module scope (see OCIARegistration: defined
// inside a component they remount on every keystroke and drop input focus).
const Subsection = ({ title, children, last }) => (
  <Box sx={{ mb: last ? 0 : 3.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>{title}</Typography>
    {children}
  </Box>
);

const ContactBlock = ({ index, value, setField }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth required={index === 0} label="Name" value={value.name} onChange={(e) => setField(`emergencyContacts.${index}.name`, e.target.value)} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth label="Address" value={value.address} onChange={(e) => setField(`emergencyContacts.${index}.address`, e.target.value)} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth required={index === 0} type="tel" label="Phone (day)" value={value.phoneDay} onChange={(e) => setField(`emergencyContacts.${index}.phoneDay`, e.target.value)} />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField fullWidth type="tel" label="Phone (evening)" value={value.phoneEvening} onChange={(e) => setField(`emergencyContacts.${index}.phoneEvening`, e.target.value)} />
    </Grid>
  </Grid>
);

export const renderStep = ({ step, form, setField, setForm, event }) => {
  switch (step) {
    case 0:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>About You</Typography>
          <Subsection title="Participant">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="First Name" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Last Name" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required type="date" label="Date of Birth" InputLabelProps={{ shrink: true }} value={form.dateOfBirth} onChange={(e) => setField('dateOfBirth', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required type="email" label="Email" helperText="Your confirmation will be sent here." value={form.email} onChange={(e) => setField('email', e.target.value)} /></Grid>
            </Grid>
          </Subsection>

          <Subsection title="Which price applies to you?">
            <FormControl>
              <RadioGroup value={form.studentCategory} onChange={(e) => setField('studentCategory', e.target.value)}>
                <FormControlLabel value="rowan-undergrad" control={<Radio />} label={`Rowan University undergraduate — ${formatCurrency(event?.pricing?.rowanUndergrad)}`} />
                <FormControlLabel value="other" control={<Radio />} label={`All other students — ${formatCurrency(event?.pricing?.otherStudents)}`} />
              </RadioGroup>
            </FormControl>
          </Subsection>

          <Subsection title="Roommate Choice(s), 2-4 in a room" last>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Write the name(s) of your roommate choice(s).</Typography>
            <Grid container spacing={2}>
              {form.roommateChoices.map((name, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <TextField fullWidth label={`Roommate ${i + 1}`} value={name} onChange={(e) => setField(`roommateChoices.${i}`, e.target.value)} />
                </Grid>
              ))}
            </Grid>
          </Subsection>
        </Box>
      );
    case 1:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Emergency Contacts</Typography>
          <Subsection title="Emergency Contact 1">
            <ContactBlock index={0} value={form.emergencyContacts[0]} setField={setField} />
          </Subsection>
          <Subsection title="Emergency Contact 2 (optional)" last>
            <ContactBlock index={1} value={form.emergencyContacts[1]} setField={setField} />
          </Subsection>
        </Box>
      );
    case 2:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Medical & Insurance</Typography>
          <Subsection title="Primary Doctor">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Primary Doctor" value={form.primaryDoctor.name} onChange={(e) => setField('primaryDoctor.name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="tel" label="Phone" value={form.primaryDoctor.phone} onChange={(e) => setField('primaryDoctor.phone', e.target.value)} /></Grid>
            </Grid>
          </Subsection>

          <Subsection title="Medications">
            {form.medications.map((m, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Medication {i + 1}</Typography>
                  {form.medications.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => setForm(prev => ({ ...prev, medications: prev.medications.filter((_, idx) => idx !== i) }))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Name of Medication" value={m.name} onChange={(e) => setField(`medications.${i}.name`, e.target.value)} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Prescribing Physician" value={m.prescribingPhysician} onChange={(e) => setField(`medications.${i}.prescribingPhysician`, e.target.value)} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="tel" label="Phone" value={m.phone} onChange={(e) => setField(`medications.${i}.phone`, e.target.value)} /></Grid>
                </Grid>
              </Paper>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setForm(prev => ({ ...prev, medications: [...prev.medications, emptyMedication()] }))}>
              Add another medication
            </Button>
          </Subsection>

          <Subsection title="Medical Conditions">
            <TextField fullWidth multiline minRows={3} label="Medical Conditions" value={form.medicalConditions} onChange={(e) => setField('medicalConditions', e.target.value)} />
          </Subsection>

          <Subsection title="Allergies">
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField fullWidth multiline label="To medicines" value={form.allergies.medicines} onChange={(e) => setField('allergies.medicines', e.target.value)} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline label="Other" value={form.allergies.other} onChange={(e) => setField('allergies.other', e.target.value)} /></Grid>
            </Grid>
          </Subsection>

          <Subsection title="Insurance" last>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Insurance Carrier" value={form.insurance.carrier} onChange={(e) => setField('insurance.carrier', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Plan" value={form.insurance.plan} onChange={(e) => setField('insurance.plan', e.target.value)} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Policy #" value={form.insurance.policyNumber} onChange={(e) => setField('insurance.policyNumber', e.target.value)} /></Grid>
            </Grid>
          </Subsection>
        </Box>
      );
    case 3:
      return (
        <Box>
          <Typography variant="h6" gutterBottom>Consent to Medical Treatment, Release and Indemnity</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2"><strong>Parish:</strong> {event?.parishName} (the &ldquo;Parish&rdquo;)</Typography>
            <Typography variant="body2"><strong>Youth/Young Adult Organization:</strong> {event?.organizationName} (the &ldquo;Parish Organization&rdquo;)</Typography>
            {event?.schedule?.dates && (
              <Typography variant="body2"><strong>Dates of the event or trip:</strong> {event.schedule.dates}</Typography>
            )}
            {event?.schedule?.optionalDates && (
              <Typography variant="body2"><strong>(Optional):</strong> {event.schedule.optionalDates}</Typography>
            )}
          </Paper>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {(event?.consentText || '').split(/\n\s*\n/).map((para, i) => (
              <Typography key={i} variant="body2" paragraph sx={{ textAlign: 'justify' }}>{para}</Typography>
            ))}
          </Box>
          <FormControlLabel
            sx={{ alignItems: 'flex-start', mb: 2 }}
            control={<Checkbox sx={{ pt: 0 }} checked={form.consent.agreed} onChange={(e) => setField('consent.agreed', e.target.checked)} />}
            label="I have read this agreement carefully and I agree to it."
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth required label="Participant's Signature (type your full name)" value={form.signature.name} onChange={(e) => setField('signature.name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Date" value={new Date().toLocaleDateString('en-US')} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>Typing your name above serves as your signature.</Alert>
        </Box>
      );
    default:
      return null;
  }
};
